/**
 * The function removes null, undefined, and empty properties from an object recursively,
 * except for the properties specified in the whitelist.
 * @param {any} object - The input object from which you want to remove null, undefined, and empty properties.
 * @param {string[]} [whitelist=[]] - An optional array of property names that should not be deleted if empty.
 * @returns An object with null, undefined, and empty properties removed, except for the properties in the whitelist.
 */
export function removeNullAndUndefinedProperties(object: any, whitelist: string[] = []): any {
  // Check if the input is null, undefined, or an empty string, return null for those cases
  if (object === null || object === undefined || object === '') {
    return null;
  }

  // Handle arrays: map over items, remove unwanted values, and filter out nulls after the transformation
  if (Array.isArray(object)) {
    return object.map((item) => removeNullAndUndefinedProperties(item, whitelist)).filter((item) => item !== null && item !== undefined && item !== '');
  }

  // For objects: create a new object, recursively remove unwanted values, and include properties that aren't null, undefined, or an empty string
  if (typeof object === 'object') {
    const result: any = {};
    for (const key of Object.keys(object)) {
      const value = removeNullAndUndefinedProperties(object[key], whitelist);
      if (whitelist.includes(key) || (value !== null && value !== undefined && value !== '')) {
        result[key] = value;
      }
    }
    return result;
  }

  // Return the object for non-object and non-array types that aren't null, undefined, or an empty string
  return object;
}

/**
 * Function to sort object keys
 * @param obj as Object
 * @returns Object
 */

export function sortObjectByKeys(obj: any) {
  return Object.keys(obj)
    .sort()
    .reduce((accumulator, key) => {
      accumulator[key] = obj[key];
      return accumulator;
    }, {});
}

/**
 * A function that takes an object and an array of properties to exclude and returns a new object
 *
 * @param object
 * @param excludeKeys
 * @returns new object without excluded keys
 */

export function OmitProperties(object: NonNullable<unknown>, excludeKeys: Set<string>): NonNullable<unknown> {
  const filteredPairs = Object.entries(object).filter(([key]) => !excludeKeys.has(key));
  return Object.fromEntries(filteredPairs);
}

/**
 * A function that takes an object and an array of properties to include and returns a new object
 *
 * @returns new object with only included keys
 * @param obj
 */
export function checkIfObjectIsEmpty(obj: any): boolean {
  return Object.keys(obj).length === 0;
}

/**
 * Updates an original object with values from an updated record of the same type,
 * only modifying properties that exist in both objects and have defined values.
 *
 * @param original - The original object to update
 * @param updatedRecord - The object containing updated values
 * @returns A new object with updated properties
 */
export function updateObjectProperties<T extends object>(original: T, updatedRecord: Partial<T>): T {
  const result = { ...original };

  for (const key in updatedRecord) {
    if (key in original && key in updatedRecord && updatedRecord[key] !== undefined && updatedRecord[key] !== null) {
      (result as any)[key] = updatedRecord[key];
    }
  }

  return result;
}

/**
 * A function that takes two objects and merges them together, updating the original object with the values from the updated object.
 * @param originalEntity
 * @param updatedEntity
 * @returns merged object
 */
export function mergeEntities<T extends object>(originalEntity: T, updatedEntity: Partial<T>): T {
  const merged = {} as T;

  Object.keys(originalEntity).forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(updatedEntity, key) && updatedEntity[key as keyof T] !== undefined) {
      merged[key as keyof T] = updatedEntity[key as keyof T] as T[keyof T];
    } else {
      merged[key as keyof T] = originalEntity[key as keyof T];
    }
  });

  return merged;
}
